import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUsageStore } from '../store/usageStore';

const UsageDisplay: React.FC = () => {
  const { usage, loading, fetchUsage } = useUsageStore();

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  if (loading || !usage) {
    return null;
  }

  const getProgressPercentage = (used: number, limit: number) => {
    if (limit === -1 || limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatResetTime = (iso: string | null | undefined) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMs = d.getTime() - now.getTime();
      const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
      const diffHours = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      const diffMins = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)));
      if (diffDays > 0) return `in ${diffDays}d ${diffHours}h`;
      if (diffHours > 0) return `in ${diffHours}h ${diffMins}m`;
      return `in ${diffMins} min`;
    } catch {
      return 'at start of next month';
    }
  };

  const isBlocked = usage.is_search_blocked ?? (usage.tier === 'free' && usage.searches_remaining <= 0);
  const planLabel = usage.tier.charAt(0).toUpperCase() + usage.tier.slice(1);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
      {/* Plan badge - always visible */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
            Plan: {planLabel}
          </span>
          {usage.subscription_status && (
            <span className="text-xs font-medium text-slate-500 capitalize">
              • {usage.subscription_status}
            </span>
          )}
        </div>
        <Link to="/pricing" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
          Change plan →
        </Link>
      </div>

      {/* Credits - prominent display */}
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Your Credits</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-slate-700">Searches</span>
            <span className="font-bold text-slate-900">
              {usage.searches_limit === -1 ? (
                <span className="text-indigo-600">∞ Unlimited</span>
              ) : (
                <>
                  <span className={usage.searches_remaining <= 0 ? 'text-rose-600' : 'text-slate-900'}>
                    {usage.searches_remaining}
                  </span>
                  <span className="text-slate-400 font-normal"> remaining</span>
                  <span className="text-slate-400 font-normal">
                    {' '}/ {usage.searches_limit} {usage.tier === 'basic' ? 'this month' : 'today'}
                  </span>
                </>
              )}
            </span>
          </div>
          {usage.searches_limit !== -1 && (
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${getProgressPercentage(usage.searches_used, usage.searches_limit)}%`,
                  backgroundColor: usage.searches_remaining <= 0 ? '#e11d48' : '#4f46e5',
                }}
              />
            </div>
          )}
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-slate-700">Leads</span>
            <span className="font-bold text-slate-900">
              {usage.leads_limit === -1 ? (
                <span className="text-indigo-600">∞ Unlimited</span>
              ) : (
                <>
                  <span className={usage.leads_remaining <= 0 ? 'text-rose-600' : 'text-slate-900'}>
                    {usage.leads_remaining}
                  </span>
                  <span className="text-slate-400 font-normal"> remaining</span>
                  <span className="text-slate-400 font-normal"> / {usage.leads_limit}</span>
                </>
              )}
            </span>
          </div>
          {usage.leads_limit !== -1 && (
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${getProgressPercentage(usage.leads_used, usage.leads_limit)}%`,
                  backgroundColor: usage.leads_remaining <= 0 ? '#e11d48' : '#4f46e5',
                }}
              />
            </div>
          )}
        </div>
      </div>
      {/* Your plan summary - only what applies to user's tier */}
      <p className="text-xs text-slate-500 mt-3">
        {usage.tier === 'free' && (
          <>Your plan: {usage.searches_limit} searches/day, {usage.leads_limit} leads max</>
        )}
        {usage.tier === 'basic' && (
          <>Your plan: {usage.searches_limit} searches/month, {usage.leads_limit} leads/month</>
        )}
        {(usage.tier === 'pro' || usage.tier === 'enterprise') && (
          <>Your plan: unlimited searches and leads</>
        )}
      </p>
      {usage.credits_reset_at && (usage.tier === 'free' || usage.tier === 'basic') && (
        <p className="text-xs text-slate-500 mt-1">
          Resets {formatResetTime(usage.credits_reset_at)}
        </p>
      )}
      {isBlocked && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-800 font-medium mb-2">Out of search credits</p>
          <p className="text-xs text-amber-700 mb-3">
            {usage.tier === 'free'
              ? 'Wait for daily reset or upgrade for more searches.'
              : usage.tier === 'basic'
              ? 'Wait for monthly reset or upgrade for unlimited searches.'
              : 'Upgrade for unlimited searches.'}
          </p>
          <Link
            to="/pricing"
            className="inline-block px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Upgrade now
          </Link>
        </div>
      )}
    </div>
  );
};

export default UsageDisplay;
